import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchViewPresets,
  createViewPreset,
  updateViewPreset,
  deleteViewPreset,
  setDefaultPreset,
  unsetDefaultPreset,
} from '@/lib/api/view-presets'
import { toast } from 'sonner'

/**
 * Query key factory for view presets
 */
export const viewPresetKeys = {
  all: ['view-presets'] as const,
  lists: () => [...viewPresetKeys.all, 'list'] as const,
  list: (locationId: string) => [...viewPresetKeys.lists(), locationId] as const,
}

/**
 * Fetch all view presets for a location
 */
export function useViewPresets(locationId: string | undefined) {
  return useQuery({
    queryKey: viewPresetKeys.list(locationId || ''),
    queryFn: () => fetchViewPresets(locationId!),
    enabled: !!locationId,
  })
}

/**
 * Create a new view preset
 */
export function useCreateViewPreset() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: (data: {
      locationId: string
      name: string
      center_lng: number
      center_lat: number
      zoom: number
      pitch: number
      bearing: number
    }) =>
      createViewPreset({
        organization_id: currentOrganization!.id,
        location_id: data.locationId,
        name: data.name,
        center_lng: data.center_lng,
        center_lat: data.center_lat,
        zoom: data.zoom,
        pitch: data.pitch,
        bearing: data.bearing,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: viewPresetKeys.list(variables.locationId) })
      toast.success('View saved')
    },
    onError: (error: Error) => {
      toast.error('Failed to save view', { description: error.message })
    },
  })
}

/**
 * Update an existing view preset
 */
export function useUpdateViewPreset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id,  data }: {
      id: string
      locationId: string
      data: Parameters<typeof updateViewPreset>[1]
    }) => updateViewPreset(id, data),
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({ queryKey: viewPresetKeys.list(locationId) })
    },
    onError: (error: Error) => {
      toast.error('Failed to update view', { description: error.message })
    },
  })
}

/**
 * Soft delete a view preset
 */
export function useDeleteViewPreset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id }: { id: string; locationId: string }) =>
      deleteViewPreset(id),
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({ queryKey: viewPresetKeys.list(locationId) })
      toast.success('View deleted')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete view', { description: error.message })
    },
  })
}

/**
 * Set a preset as default (two-step: unset old, set new)
 */
export function useSetDefaultPreset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ presetId, locationId }: { presetId: string; locationId: string }) =>
      setDefaultPreset(presetId, locationId),
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({ queryKey: viewPresetKeys.list(locationId) })
      toast.success('Default view updated')
    },
    onError: (error: Error) => {
      toast.error('Failed to set default view', { description: error.message })
    },
  })
}

/**
 * Unset a preset's default flag
 */
export function useUnsetDefaultPreset() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ presetId }: { presetId: string; locationId: string }) =>
      unsetDefaultPreset(presetId),
    onSuccess: (_, { locationId }) => {
      queryClient.invalidateQueries({ queryKey: viewPresetKeys.list(locationId) })
    },
    onError: (error: Error) => {
      toast.error('Failed to update view', { description: error.message })
    },
  })
}
